"use client";

import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import i18next from '@/lib/i18n';

const t = (k: string): string => String(i18next.t(k));

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url?: string;
  urls?: string[];
  alt?: string;
  titleClose?: string;
};

export const ImagePreviewDialog = ({ open, onOpenChange, url, urls, alt, titleClose }: Props) => {
  const [loaded, setLoaded] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // 确定要显示的图片列表
  const imageUrls = useMemo(() => {
    if (Array.isArray(urls) && urls.length > 0) {
      return urls.filter(Boolean);
    }
    if (url) {
      return [url];
    }
    return [];
  }, [urls, url]);

  // 当前显示的图片URL
  const currentUrl = useMemo(() => {
    return imageUrls[currentIndex] ?? imageUrls[0];
  }, [imageUrls, currentIndex]);

  // 当对话框打开时，重置到第一张图片
  useEffect(() => {
    if (open) {
      setCurrentIndex(0);
      setLoaded(false);
    }
  }, [open]);

  // 当url或urls变化时，更新当前索引
  useEffect(() => {
    if (url && imageUrls.includes(url)) {
      const index = imageUrls.indexOf(url);
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [url, imageUrls]);

  // 键盘快捷键支持
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // 如果焦点在输入框等可编辑元素上，不处理箭头键
      const target = e.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleNext();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        onOpenChange(false);
      }
    };

    // 使用 capture 阶段捕获事件，确保在其他监听器之前处理
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [open, currentIndex, imageUrls.length]);

  const handlePrevious = () => {
    if (imageUrls.length <= 1) return;
    setLoaded(false);
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : imageUrls.length - 1));
  };

  const handleNext = () => {
    if (imageUrls.length <= 1) return;
    setLoaded(false);
    setCurrentIndex((prev) => (prev < imageUrls.length - 1 ? prev + 1 : 0));
  };

  const hasMultipleImages = imageUrls.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        showCloseButton={false} 
        className="sm:max-w-6xl p-0 bg-transparent border-0 shadow-none ring-0"
        onPointerDown={(e) => {
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        {currentUrl ? (
          <div 
            className="relative max-h-[85vh] max-w-[90vw] flex items-center justify-center"
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="relative inline-flex items-center justify-center overflow-hidden rounded-2xl border-l-[5px] border-r-[5px] border-b-[5px] border-t-[5px] border-border bg-background/80 backdrop-blur-sm">
              <Image
                src={currentUrl}
                alt={alt ?? 'Preview image'}
                width={1600}
                height={1600}
                onLoad={() => setLoaded(true)}
                className="block h-auto w-auto max-h-[85vh] max-w-[90vw] object-contain"
              />
              {!loaded && (
                <div className="absolute inset-0 grid place-items-center">
                  <span className="text-xs text-muted-foreground">Loading...</span>
                </div>
              )}
              
              {/* 关闭按钮 */}
              <Button
                aria-label={titleClose ?? 'Close preview'}
                onClick={() => onOpenChange(false)}
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 rounded-full bg-background text-foreground border shadow-md hover:bg-background/90 z-10"
              >
                <XIcon size={16} />
              </Button>

              {/* 左箭头按钮 */}
              {hasMultipleImages && (
                <>
                  <Button
                    aria-label={t("Previous image")}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handlePrevious();
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    variant="ghost"
                    size="icon"
                    className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-background/80 text-foreground border shadow-md hover:bg-background/90 z-10"
                  >
                    <ChevronLeftIcon size={20} />
                  </Button>

                  {/* 右箭头按钮 */}
                  <Button
                    aria-label={t("Next image")}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleNext();
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-background/80 text-foreground border shadow-md hover:bg-background/90 z-10"
                  >
                    <ChevronRightIcon size={20} />
                  </Button>

                  {/* 图片计数器 */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-background/80 px-3 py-1 text-xs text-muted-foreground border shadow-md backdrop-blur-sm z-10">
                    {currentIndex + 1} / {imageUrls.length}
                  </div>
                </>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};


